import {
  ColumnDef,
  getCoreRowModel,
  OnChangeFn,
  RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';
import { useMemo } from 'react';

import { useTableContext } from '@/contexts/table-context';

interface UseDataTableProps<TData> {
  columns: ColumnDef<TData>[];
  data: TData[];
  rowCount: number;
  externalIdKey?: keyof TData;
  pinnedLeftColumns?: string[];
}

export function useDataTable<TData>({
  columns,
  data,
  rowCount,
  externalIdKey,
  pinnedLeftColumns,
}: UseDataTableProps<TData>) {
  const {
    pagination,
    setPagination,
    rowSelection,
    setRowSelection,
    sorting,
    setSorting,
  } = useTableContext();

  // Compute the set of row IDs currently visible on this page so we can
  // distinguish "off-page" (previously selected on another page) keys from
  // "on-page" keys when merging selection updates.
  const currentPageIds = useMemo(
    () =>
      new Set(
        data.map((row, index) =>
          externalIdKey ? String(row[externalIdKey]) : String(index),
        ),
      ),
    [data, externalIdKey],
  );

  // Wrap TanStack Table's onRowSelectionChange so that toggling rows on the
  // current page never discards selections made on other pages.
  //
  // TanStack Table calls this handler with the *full* next selection state for
  // all rows it knows about (i.e. only the current page).  Without this wrapper
  // that would silently drop the IDs of rows selected on other pages, because
  // those IDs are not in the current page's row model and therefore not present
  // in the value TanStack produces.
  const handleRowSelectionChange: OnChangeFn<RowSelectionState> = updater => {
    setRowSelection(prev => {
      // Resolve the new state TanStack wants to set (covers current page only)
      const nextPageSelection =
        typeof updater === 'function' ? updater(prev) : updater;

      // Keep all keys that belong to OTHER pages (not visible right now)
      const offPageSelection = Object.fromEntries(
        Object.entries(prev).filter(([id]) => !currentPageIds.has(id)),
      );

      // Merge: off-page selections are preserved; current-page selection
      // reflects exactly what TanStack computed.
      return { ...offPageSelection, ...nextPageSelection };
    });
  };

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),

    // Pagination options
    manualPagination: true,
    onPaginationChange: setPagination,
    rowCount,

    // Selection options
    onRowSelectionChange: handleRowSelectionChange,
    getRowId: (row, index) =>
      externalIdKey ? String(row[externalIdKey]) : String(index),

    // Sorting options
    manualSorting: true,
    onSortingChange: setSorting,

    initialState: {
      columnPinning: {
        left: pinnedLeftColumns ?? [],
      },
    },

    state: {
      pagination,
      rowSelection,
      sorting,
    },
  });

  return { table };
}
